import { api } from "./api";

async function evmRpc(chain, method, params) {
  const { data } = await api.post(`/rpc/evm/${chain}`, { jsonrpc: "2.0", id: 1, method, params });
  if (data.error) throw new Error(data.error.message);
  return data.result;
}

async function solRpc(method, params) {
  const { data } = await api.post(`/rpc/sol`, { jsonrpc: "2.0", id: 1, method, params });
  if (data.error) throw new Error(data.error.message);
  return data.result;
}

// Native balance (coin units) per chain
export async function fetchBalance(chain, address) {
  if (!address) return 0;
  try {
    if (chain.family === "evm") {
      const hex = await evmRpc(chain.id, "eth_getBalance", [address, "latest"]);
      return Number(BigInt(hex)) / 1e18;
    }
    if (chain.family === "sol") {
      const res = await solRpc("getBalance", [address]);
      return (res?.value ?? 0) / 1e9;
    }
    if (chain.family === "btc") {
      const { data } = await api.get(`/rpc/btc/address/${address}`);
      const cs = data.chain_stats || {};
      const ms = data.mempool_stats || {};
      const sats = (cs.funded_txo_sum || 0) - (cs.spent_txo_sum || 0) + (ms.funded_txo_sum || 0) - (ms.spent_txo_sum || 0);
      return sats / 1e8;
    }
    if (chain.family === "tron") {
      const { data } = await api.get(`/rpc/tron/v1/accounts/${address}`);
      const acc = (data.data && data.data[0]) || {};
      return (acc.balance || 0) / 1e6;
    }
  } catch (e) {
    return 0;
  }
  return 0;
}

export { evmRpc, solRpc };

// ---- Token balances (ERC-20 / TRC-20 / SPL) ----
async function erc20Balance(chainId, token, address) {
  const data = "0x70a08231" + address.replace(/^0x/, "").toLowerCase().padStart(64, "0");
  const hex = await evmRpc(chainId, "eth_call", [{ to: token.address, data }, "latest"]);
  if (!hex || hex === "0x") return 0;
  return Number(BigInt(hex)) / 10 ** token.decimals;
}

async function tronTokenMap(address) {
  const { data } = await api.get(`/rpc/tron/v1/accounts/${address}`);
  const acc = (data.data && data.data[0]) || {};
  const map = {};
  (acc.trc20 || []).forEach((o) => {
    const [k, v] = Object.entries(o)[0];
    map[k] = v;
  });
  return map;
}

async function splBalance(mint, owner) {
  const res = await solRpc("getTokenAccountsByOwner", [owner, { mint }, { encoding: "jsonParsed" }]);
  const value = (res && res.value) || [];
  let total = 0;
  value.forEach((a) => {
    total += a.account.data.parsed.info.tokenAmount.uiAmount || 0;
  });
  return total;
}

export async function fetchAssetBalance(asset, accounts) {
  const { getChain, addressFor } = await import("./chains");
  const addr = addressFor(accounts, asset.chain);
  if (!addr) return 0;
  try {
    if (asset.kind === "native") return await fetchBalance(getChain(asset.chain), addr);
    const t = asset.token;
    if (t.std === "erc20") return await erc20Balance(asset.chain, t, addr);
    if (t.std === "trc20") {
      const m = await tronTokenMap(addr);
      return Number(m[t.address] || 0) / 10 ** t.decimals;
    }
    if (t.std === "spl") return await splBalance(t.address, addr);
  } catch {
    return 0;
  }
  return 0;
}
